import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import UserLink from '../../userLink';
import CustomButton from '../../customButton';
import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar.svg';
import { formatDate } from '../../../../utility/dateUtils';
import StatusIndicator from '../../statusIndicator';
import { prepareTranslatedAbsenceRequestStatuses } from '../../../../utility/absence';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    marginRight: spacing(2),
  },
  user: {
    margin: spacing(2, 0),
  },
  dateRangeWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  calendarIcon: {
    marginTop: spacing(-1),
    marginRight: spacing(2),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
  },
  acceptButton: {
    marginLeft: spacing(-2),
  },
  dismissButton: {
    color: primary.bluish1,
  },
}));

const DATE_FORMAT = 'MMM dd';

const AbsenceRequestCard = ({
  translations,
  isDoubleApprove,
  absenceRequest = {},
  onAccept,
  onDismiss,
}) => {
  const classes = useStyles();

  const { user, isAccessible, dates, days, status } = absenceRequest;
  const statuses = prepareTranslatedAbsenceRequestStatuses(
    translations.statuses
  );
  const currentStatus = statuses.find(item => item.id === status);

  const dateRange =
    days === 1
      ? `1 day, ${formatDate(dates.at(0), DATE_FORMAT)}`
      : `${days} days, ${formatDate(dates.at(0), DATE_FORMAT)} - ${formatDate(
          dates.at(-1),
          DATE_FORMAT
        )}`;

  const handleAccept = useCallback(() => {
    return onAccept(absenceRequest.id);
  }, [absenceRequest, onAccept]);

  const handleDismiss = useCallback(() => {
    onDismiss(absenceRequest.id);
  }, [absenceRequest, onDismiss]);

  return (
    <div className={classes.root}>
      <div className={classes.titleWrapper}>
        <Typography className={classes.title} variant="overline">
          {translations.title.toUpperCase()}
        </Typography>
        <StatusIndicator
          status={currentStatus}
          isDoubleApprove={isDoubleApprove}
        />
      </div>
      <UserLink
        className={classes.user}
        user={user}
        avatarProps={{
          variant: 'subtitle1',
          extraSmall: false,
          small: true,
        }}
        isUserAccessible={isAccessible}
      />
      <div className={classes.dateRangeWrapper}>
        <CalendarIcon className={classes.calendarIcon} />
        <Typography variant="body2">{dateRange}</Typography>
      </div>
      <div className={classes.actions}>
        <CustomButton
          className={classes.acceptButton}
          type="textRoundedSmall"
          onClick={handleAccept}
        >
          {translations.accept}
        </CustomButton>
        <CustomButton
          className={classes.dismissButton}
          type="textRoundedSmall"
          onClick={handleDismiss}
        >
          {translations.dismiss}
        </CustomButton>
      </div>
    </div>
  );
};

AbsenceRequestCard.propTypes = {
  absenceRequest: PropTypes.object,
  isDoubleApprove: PropTypes.bool.isRequired,
};

export default AbsenceRequestCard;
